// Aufgeteilt aus dem früheren 2600-Zeilen-Monolithen `musicXmlDom.ts` (Import-Pfad unverändert:
// `@/composables/musicXmlDom` zeigt auf `index.ts`). Abhängigkeitsregel im Ordner: geteilte
// Basis-Helfer liegen in `core.ts`; gerichtete Geschwister-Imports sind erlaubt, ZYKLEN nicht
// (aktuell: bass→chordInference, notes→core, diatonic→notes, structure→notes,
// lyrics→bass, clipboard→structure, fragment→structure).
import {type ClipboardNote, type NoteInfo, buildNoteElement, chordGroupOf, doublePartDivisions, findPartAndMeasureNote, getPartDivisions, getVoiceNotes, noteDurationTicks, parseMusicXml, pitchOfNote, pitchToMidi, pitchToSemitone, removeBeat, semitoneToPitch, serializeMusicXml, setPitch, ticksToClosestType, ticksToRestElements, voiceOf} from './core';
import {splitNoteElement} from './structure';



// ============ Kopieren / Einfügen ============

/** Liest Tonhöhe + Rhythmus einer bereits aufgelösten `<note>` in eine Zwischenablage-Nutzlast. */
function readPayloadFromNote(part: Element, note: Element): ClipboardNote {
    const isRest = note.querySelector('rest') !== null;
    const pitchEl = note.querySelector('pitch');
    return {
        isRest,
        step: pitchEl?.querySelector('step')?.textContent || '',
        alter: parseInt(pitchEl?.querySelector('alter')?.textContent || '0', 10),
        octave: parseInt(pitchEl?.querySelector('octave')?.textContent || '4', 10),
        type: note.querySelector('type')?.textContent || '',
        dots: note.querySelectorAll('dot').length,
        durationTicks: noteDurationTicks(note),
        sourceDivisions: getPartDivisions(part) || 1,
    };
}

/** Liest Tonhöhe + Rhythmus der gewählten Note für die Zwischenablage (Strg+C). */
export function readNotePayload(musicXml: string, noteInfo: NoteInfo): ClipboardNote | null {
    try {
        const doc = parseMusicXml(musicXml);
        const found = findPartAndMeasureNote(doc, noteInfo);
        if (!found) return null;
        return readPayloadFromNote(found.part, found.note);
    } catch (e) {
        console.error('Error reading note payload:', e);
        return null;
    }
}

/** Liest eine ganze Auswahl in eine geordnete Sequenz von Zwischenablage-Nutzlasten (Mehrfach-
 *  Kopieren). Nicht auflösbare Einträge werden übersprungen; die Reihenfolge folgt `infos`. */
export function readNotesPayload(musicXml: string, infos: NoteInfo[]): ClipboardNote[] {
    try {
        const doc = parseMusicXml(musicXml);
        const out: ClipboardNote[] = [];
        for (const info of infos) {
            const found = findPartAndMeasureNote(doc, info);
            if (found) out.push(readPayloadFromNote(found.part, found.note));
        }
        return out;
    } catch (e) {
        console.error('Error reading notes payload:', e);
        return [];
    }
}

/**
 * Klingende MIDI-Tonhöhen der angegebenen Noten – die Datenquelle des Vorschau-Tons
 * (`osmd/notePreviewTone.ts`). Gelesen wird aus dem übergebenen XML, also **nach** einer
 * Bearbeitung: so erklingt das Ergebnis und nicht der Ausgangszustand.
 *
 * Standardmäßig klingt **nur die adressierte Note**, nicht ihre Akkord-Nachbarn – gehört werden
 * soll, was sich geändert hat. `includeChordNeighbours` kehrt das um und nimmt die ganze
 * Akkord-Gruppe mit (`chordGroupOf`); das brauchen die Diatonik-Operationen, bei denen der
 * **Akkord** die Bearbeitung ist und der neu eingefügte Ton gar nicht in der Auswahl steht.
 *
 * Pausen und nicht auflösbare Adressen werden übersprungen, das Ergebnis ist dedupliziert
 * (dieselbe Tonhöhe doppelt anzuschlagen wäre nur lauter, nicht informativer). Wirft nie.
 */
export function readSoundingPitches(
    musicXml: string | null,
    infos: readonly NoteInfo[],
    options: { includeChordNeighbours?: boolean } = {},
): number[] {
    if (!musicXml || !infos.length) return [];
    try {
        const doc = parseMusicXml(musicXml);
        const seen = new Set<number>();
        const out: number[] = [];
        for (const info of infos) {
            const found = findPartAndMeasureNote(doc, info);
            if (!found) continue;
            const group = options.includeChordNeighbours ? chordGroupOf(found.note) : [found.note];
            for (const noteEl of group) {
                const pitch = pitchOfNote(noteEl);
                if (!pitch || !pitch.step) continue;
                const midi = pitchToMidi(pitch.step, pitch.alter, pitch.octave);
                if (seen.has(midi)) continue;
                seen.add(midi);
                out.push(midi);
            }
        }
        return out;
    } catch (e) {
        console.error('Error reading sounding pitches:', e);
        return [];
    }
}

/**
 * Transponiert alle übergebenen Noten um `deltaSemitones` Halbtöne (±). Die Tonhöhe wird direkt aus
 * `<pitch>` gelesen (nicht aus `NoteInfo.semitones`, das bei XML-abgeleiteten Infos 0 ist), per
 * `pitchToSemitone` → `+delta` → `semitoneToPitch` neu gesetzt (Oktaven wickeln korrekt). Schreibweise
 * schwarzer Tasten nach **Richtung**: rauf → Kreuz (C → C#), runter → B (D → Db); reine Oktaven (±12)
 * behalten die vorhandene Schreibweise (F#4 −12 → F#3). Ein veraltetes `<accidental>` entfernt
 * `setPitch`. Pausen werden übersprungen. Dauer bleibt unberührt → **reihenfolge-unabhängig** und die
 * Taktlängen-Invariante bleibt erhalten. Es wird **keine** Farbe geschrieben.
 */
export function transposeNotesInMusicXml(musicXml: string, infos: NoteInfo[], deltaSemitones: number): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        for (const info of infos) {
            if (info.isRest) continue;
            const found = findPartAndMeasureNote(doc, info);
            if (!found) continue;
            const pitchEl = found.note.querySelector('pitch');
            if (!pitchEl) continue;
            const step = pitchEl.querySelector('step')?.textContent || 'C';
            const alter = parseInt(pitchEl.querySelector('alter')?.textContent || '0', 10);
            const octave = parseInt(pitchEl.querySelector('octave')?.textContent || '4', 10);
            if (deltaSemitones % 12 === 0) {
                // Reine Oktave: Schreibweise (F#/Gb) unverändert lassen, nur die Oktave verschieben.
                setPitch(doc, found.note, step, alter, octave + deltaSemitones / 12);
                continue;
            }
            // Richtung bestimmt die Schreibweise schwarzer Tasten: rauf → Kreuz, runter → B.
            const p = semitoneToPitch(
                pitchToSemitone(step, alter, octave) + deltaSemitones,
                deltaSemitones < 0 ? 'flat' : 'sharp',
            );
            setPitch(doc, found.note, p.step, p.alter, p.octave);
        }
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error transposing notes in MusicXML:', e);
        return null;
    }
}

/**
 * Ersetzt die Tonhöhe der Ziel-Noten `infos` durch die Tonhöhen der kopierten Sequenz `clips` –
 * **zyklisch** zugeordnet (`clips[i % clips.length]`): 1 kopierte Note → Broadcast auf alle Ziele,
 * gleich viele → 1:1, sonst wird das kopierte Muster wiederholt (z. B. 2 clips auf 5 Ziele →
 * A,B,A,B,A). Ziel-Pausen und kopierte Pausen werden **positionstreu** übersprungen (die Position
 * `i` treibt das Modulo weiter, damit die Zuordnung vorhersehbar bleibt). Nur `<pitch>` wird
 * geändert, **Dauer bleibt unberührt** → reihenfolge-unabhängig und die Taktlängen-Invariante bleibt
 * erhalten. Es wird **keine** Farbe geschrieben.
 */
export function replaceNotesPitchesInMusicXml(musicXml: string, infos: NoteInfo[], clips: ClipboardNote[]): string | null {
    try {
        if (!clips.length || !infos.length) return musicXml;
        const doc = parseMusicXml(musicXml);
        for (let i = 0; i < infos.length; i++) {
            const info = infos[i]!;
            const clip = clips[i % clips.length]!;
            if (info.isRest || clip.isRest) continue;
            const found = findPartAndMeasureNote(doc, info);
            if (!found) continue;
            setPitch(doc, found.note, clip.step, clip.alter, clip.octave);
        }
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error replacing note pitches in MusicXML:', e);
        return null;
    }
}

/**
 * Ersetzt die **einzelne** Ziel-Note `target` durch die kopierte Sequenz `seq` – **inklusive deren
 * Rhythmus** (z. B. Viertel → zwei Achtel). Die Taktlänge bleibt exakt erhalten: die Sequenz wird am
 * **Onset** der Ziel-Note eingefügt (nicht dahinter) und ihre Dauer aus der Ziel-Note (+ folgenden
 * Noten der Stimme, wenn die Sequenz länger ist) „bezahlt"; ein Überschuss wird als Standard-Pausen
 * aufgefüllt, ein Überlauf über den restlichen Takt-Platz **geclampt** (verworfen). Wiederverwendung
 * derselben pay/fill/clamp-Bausteine wie `insertOneAfter` – **keine** parallele Dauer-Mathematik.
 *
 * Ein einzelnes `seq`-Element gleicher Dauer ergibt exakt das reine Tonhöhen-Ersetzen (keine
 * Regression). Eine Ziel-**Pause** kann so durch Noten ersetzt werden; `seq`-Pausen werden als
 * `<rest>` eingesetzt.
 */
export function replaceNoteWithSequenceInMusicXml(musicXml: string, target: NoteInfo, seq: ClipboardNote[]): string | null {
    try {
        if (!seq.length) return musicXml;
        const doc = parseMusicXml(musicXml);
        const found = findPartAndMeasureNote(doc, target);
        if (!found) return null;
        const {part, note} = found;
        const voice = voiceOf(note);
        const measure = note.closest('measure');
        if (!measure) return null;

        // Divisions-Präzision: alle seq-Dauern müssen ganzzahlig in Ziel-Ticks aufgehen, sonst
        // rundet z. B. „Achtel bei divisions=1" auf 1 Tick und überfüllt den Takt. Notfalls part-weit
        // verdoppeln (wie splitNoteElement), bis es aufgeht (Guard gegen Endlosschleife).
        for (let guard = 0; guard < 8; guard++) {
            const d = getPartDivisions(part) || 1;
            const needsFiner = seq.some(c => c.sourceDivisions > 0 && (c.durationTicks * d) % c.sourceDivisions !== 0);
            if (!needsFiner) break;
            doublePartDivisions(part);
        }

        const divisions = getPartDivisions(part) || 1;
        const insTicksOf = (c: ClipboardNote): number => {
            const t = c.sourceDivisions > 0
                ? Math.round(c.durationTicks * divisions / c.sourceDivisions)
                : c.durationTicks;
            return t < 1 ? 1 : t;
        };

        // `<staff>` der Ziel-Note erben (Grand-Staff: sonst landet die neue Note im Default-Staff 1).
        const staff = note.querySelector('staff')?.textContent ?? null;
        // Verfügbarer Takt-Platz ab der Ziel-Note (Onset-Kapazität, ohne <chord>-Begleiter).
        const voiceNotes = getVoiceNotes(measure, voice);
        const refIdx = voiceNotes.indexOf(note);
        const following = voiceNotes.slice(refIdx + 1).filter(n => !n.querySelector('chord'));
        const targetTicks = noteDurationTicks(note);
        const totalAvailable = targetTicks + following.reduce((s, n) => s + noteDurationTicks(n), 0);

        // Neue Noten bauen, gierig auf den verfügbaren Platz begrenzt (Überlauf verwerfen).
        const newNotes: Element[] = [];
        let builtTicks = 0;
        for (const clip of seq) {
            if (builtTicks >= totalAvailable) break;
            const want = insTicksOf(clip);
            const room = totalAvailable - builtTicks;
            const t = Math.min(want, room);
            const clamped = t < want;
            const type = clamped ? ticksToClosestType(t, divisions) : clip.type;
            const dots = clamped ? 0 : clip.dots;
            newNotes.push(buildNoteElement(doc, clip, t, type, dots, voice, staff));
            builtTicks += t;
        }
        if (!newNotes.length) return musicXml;

        // Onset-Anchoring: die neuen Noten VOR die Ziel-Note einfügen (in Reihenfolge davor).
        for (const n of newNotes) note.before(n);
        const lastNew = newNotes[newNotes.length - 1]!;

        // Bezahlen: die Ziel-Note (inkl. ihrer <chord>-Begleiter) + so viele folgende beats wie nötig.
        let need = builtTicks;
        let removed = 0;
        for (const beat of [note, ...following]) {
            if (need <= 0) break;
            const dur = noteDurationTicks(beat);
            removed += dur;
            need -= dur;
            removeBeat(beat);
        }

        // Überschuss (mehr entfernt als eingefügt) als Standard-Pausen nach der letzten neuen Note.
        const overshoot = removed - builtTicks;
        if (overshoot > 0) {
            let anchor: Element = lastNew;
            for (const rest of ticksToRestElements(doc, overshoot, divisions, voice, staff)) {
                anchor.after(rest);
                anchor = rest;
            }
        }

        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error replacing note with sequence in MusicXML:', e);
        return null;
    }
}

/**
 * Fügt die kopierte Note direkt NACH der Zielnote ein (Strg+Umschalt+V) und hält die Taktlänge
 * exakt: die eingefügte Dauer wird durch Überschreiben der unmittelbar folgenden Elemente
 * derselben Stimme im Takt „bezahlt"; ein Überschuss wird als Standard-Pausen aufgefüllt. Reicht
 * der Platz nicht, wird die Dauer auf den freien Platz begrenzt; ist gar kein Platz frei, wird
 * längenerhaltend auf `splitNoteInMusicXml` zurückgefallen (neue Hälfte = kopierte Tonhöhe).
 */
/** DOM-basierter Kern des Einfügens: fügt `payload` längenerhaltend direkt NACH `refNote` (in
 *  `part`) ein und gibt die eingefügte `<note>` zurück (oder `null`, wenn kein Platz frei ist und
 *  auch nicht geteilt werden kann). Löst Takt/Stimme frisch aus `refNote` auf, arbeitet also
 *  korrekt, wenn er in einer Schleife wiederholt aufgerufen wird (Sequenz-Einfügen). */
function insertOneAfter(doc: Document, part: Element, refNote: Element, payload: ClipboardNote): Element | null {
    const voice = voiceOf(refNote);
    // `<staff>` der Referenz-Note erben, damit die neue Note bei Grand-Staff-Parts im richtigen Staff
    // landet (sonst Default-Staff 1). `null` bei Einzel-Staff (kein `<staff>` im Original).
    const staff = refNote.querySelector('staff')?.textContent ?? null;
    const measure = refNote.closest('measure');
    if (!measure) return null;
    const voiceNotes = getVoiceNotes(measure, voice);

    const divisions = getPartDivisions(part) || payload.sourceDivisions || 1;
    let insTicks = payload.sourceDivisions > 0
        ? Math.round(payload.durationTicks * divisions / payload.sourceDivisions)
        : payload.durationTicks;
    if (insTicks < 1) insTicks = 1;
    let insType = payload.type;
    let insDots = payload.dots;

    // Folgende Zählzeiten der Stimme im selben Takt (ohne Chord-Begleiter) = überschreibbarer Platz.
    const refIdx = voiceNotes.indexOf(refNote);
    const following = voiceNotes.slice(refIdx + 1).filter(n => !n.querySelector('chord'));
    const available = following.reduce((sum, n) => sum + noteDurationTicks(n), 0);

    // Kein Platz hinter der Auswahl → längenerhaltendes Teilen als Fallback (neue Hälfte = Tonhöhe).
    if (available <= 0) {
        const inserted = splitNoteElement(doc, part, refNote);
        if (!inserted) return null;
        if (!payload.isRest) setPitch(doc, inserted, payload.step, payload.alter, payload.octave);
        return inserted;
    }

    // Auf den verfügbaren Platz begrenzen (Takt nie überfüllen).
    if (insTicks > available) {
        insTicks = available;
        insType = ticksToClosestType(insTicks, divisions);
        insDots = 0;
    }

    const newNote = buildNoteElement(doc, payload, insTicks, insType, insDots, voice, staff);
    refNote.after(newNote);

    // Eingefügte Dauer durch Entfernen ganzer folgender Zählzeiten „bezahlen".
    let need = insTicks;
    for (const beat of following) {
        if (need <= 0) break;
        need -= noteDurationTicks(beat);
        removeBeat(beat);
    }
    // Über-Reclaim → Rest als Standard-Pausen direkt hinter der neuen Note auffüllen.
    if (need < 0) {
        let anchor: Element = newNote;
        for (const rest of ticksToRestElements(doc, -need, divisions, voice, staff)) {
            anchor.after(rest);
            anchor = rest;
        }
    }
    return newNote;
}

/**
 * Fügt die kopierte Note direkt NACH der Zielnote ein (Strg+Umschalt+V) und hält die Taktlänge
 * exakt: die eingefügte Dauer wird durch Überschreiben der unmittelbar folgenden Elemente
 * derselben Stimme im Takt „bezahlt"; ein Überschuss wird als Standard-Pausen aufgefüllt. Reicht
 * der Platz nicht, wird die Dauer auf den freien Platz begrenzt; ist gar kein Platz frei, wird
 * längenerhaltend auf `splitNoteElement` zurückgefallen (neue Hälfte = kopierte Tonhöhe).
 */
export function insertNoteAfterInMusicXml(musicXml: string, target: NoteInfo, payload: ClipboardNote): string | null {
    try {
        const doc = parseMusicXml(musicXml);
        const found = findPartAndMeasureNote(doc, target);
        if (!found) return null;
        if (!insertOneAfter(doc, found.part, found.note, payload)) return null;
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error inserting note in MusicXML:', e);
        return null;
    }
}

/**
 * Fügt eine ganze **Sequenz** kopierter Noten direkt NACH der Zielnote ein (Mehrfach-Einfügen).
 * Jedes Element wird über `insertOneAfter` längenerhaltend eingefügt; der Anker wandert jeweils auf
 * die zuletzt eingefügte Note, sodass die Sequenz in Reihenfolge hinter der Auswahl landet. Da
 * jeder Einzel-Insert die Taktlänge erhält, gilt das auch für die Sequenz (am Taktende kann ein
 * Element gekappt/geteilt werden). Gibt das serialisierte MusicXML zurück oder `null` bei Fehler.
 */
export function insertSequenceAfterInMusicXml(musicXml: string, target: NoteInfo, seq: ClipboardNote[]): string | null {
    try {
        if (!seq.length) return musicXml;
        const doc = parseMusicXml(musicXml);
        const found = findPartAndMeasureNote(doc, target);
        if (!found) return null;
        const {part} = found;
        let anchor: Element = found.note;
        for (const payload of seq) {
            const inserted = insertOneAfter(doc, part, anchor, payload);
            if (!inserted) break; // kein Platz mehr / nicht teilbar → Sequenz hier beenden
            anchor = inserted;
        }
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error inserting sequence in MusicXML:', e);
        return null;
    }
}

